// Impostazioni › Profili (tela, artboard 47): chi entra nella plancia e con quale ruolo. Solo
// l'amministratore la vede, e il server la chiude agli altri (poptown.api.roles).
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { ChoiceCards, type ChoiceCard } from '../../components/ChoiceCards';
import { Field, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import { ApiError, api, ROLE_LABEL, type Account, type Role } from '../../lib/api';
import { UserAvatar } from '../../shell/Sidebar';
import { useStore } from '../../store/StoreProvider';
import { SettingsBlock } from './SettingsBlock';

const ROLES: ChoiceCard<Role>[] = [
  {
    value: 'admin',
    title: 'Amministratore',
    hint: 'Tutto, comprese Impostazioni e Profili.',
    icon: 'key-round',
  },
  {
    value: 'user',
    title: 'Utente',
    hint: 'Usa la plancia: richieste, conversazioni, sessioni. Niente Impostazioni.',
    icon: 'user',
  },
  {
    value: 'viewer',
    title: 'Visitatore',
    hint: 'Solo lettura: guarda, non scrive e non cambia nulla.',
    icon: 'eye',
  },
];

const EMPTY = { user: '', display: '', role: 'user' as Role, password: '' };

function seen(a: Account, me: string): string {
  if (a.user === me) return 'attivo ora';
  if (!a.lastSeen) return 'mai entrato';
  return `ultimo accesso il ${new Date(a.lastSeen * 1000).toLocaleDateString('it-IT', { dateStyle: 'medium' })}`;
}

export function ProfilesTab() {
  const { state, toast } = useStore();
  const [rows, setRows] = useState<Account[]>([]);
  const [draft, setDraft] = useState<typeof EMPTY | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.accounts().then(setRows, () => setRows([]));
  }, []);
  useEffect(load, [load]);

  const fail = (title: string) => (e: unknown) =>
    toast({
      kind: 'danger',
      title,
      body: e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.',
    });

  const create = () => {
    if (!draft) return;
    setBusy(true);
    api
      .createAccount(draft)
      .then((a) => {
        toast({ kind: 'success', title: `Profilo «${a.display}» creato` });
        setDraft(null);
        load();
      })
      .catch(fail('Profilo non creato'))
      .finally(() => setBusy(false));
  };

  const setRole = (a: Account, role: Role) =>
    void api.setAccountRole(a.user, role).then(load, fail('Ruolo non cambiato'));

  const remove = (a: Account) => {
    if (!window.confirm(`Togliere il profilo «${a.display}»? Le sue sessioni si chiudono subito.`))
      return;
    void api
      .removeAccount(a.user)
      .then(() => {
        toast({ kind: 'success', title: 'Profilo tolto' });
        load();
      })
      .catch(fail('Profilo non tolto'));
  };

  return (
    <SettingsBlock
      over="Accessi"
      title={`Profili · ${rows.length}`}
      hint="Chi entra nella plancia e cosa può fare. Le modifiche valgono subito."
      control={
        !draft && (
          <Button variant="primary" icon="plus" onClick={() => setDraft(EMPTY)}>
            Nuovo profilo
          </Button>
        )
      }
    >
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((a) => (
          <div key={a.user} className="account-row">
            <UserAvatar avatar={a.avatar} name={a.display} size={40} />
            <div style={{ flex: 1, minWidth: 160, display: 'grid', gap: 2 }}>
              <span className="t-strong" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {a.display}
                <span className="role-badge" data-role={a.role}>
                  {ROLE_LABEL[a.role]}
                </span>
              </span>
              <span className="t-caption faint">
                {a.user} · {seen(a, state.user)}
              </span>
            </div>
            {a.user === state.user ? (
              <span className="settings-badge">tu</span>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  label={`Ruolo di ${a.display}`}
                  value={a.role}
                  width={160}
                  options={ROLES.map((r) => ({ value: r.value, label: r.title }))}
                  onChange={(v) => setRole(a, v as Role)}
                />
                <Button size="sm" variant="danger" icon="trash" onClick={() => remove(a)}>
                  Togli
                </Button>
              </div>
            )}
          </div>
        ))}

        {draft && (
          <div className="account-new">
            <span className="t-overline faint">Nuovo profilo</span>
            <div className="account-fields">
              <Field
                label="Nome utente"
                hint="Per entrare. Lettere minuscole, numeri, punto e trattino."
              >
                <TextField
                  value={draft.user}
                  autoComplete="off"
                  onChange={(v) => setDraft({ ...draft, user: v.toLowerCase() })}
                />
              </Field>
              <Field label="Nome visualizzato">
                <TextField
                  value={draft.display}
                  onChange={(v) => setDraft({ ...draft, display: v })}
                />
              </Field>
              <Field
                label="Password iniziale"
                hint="Almeno 8 caratteri: la persona la cambia dal suo profilo."
              >
                <TextField
                  type="password"
                  autoComplete="new-password"
                  value={draft.password}
                  onChange={(v) => setDraft({ ...draft, password: v })}
                />
              </Field>
            </div>
            <ChoiceCards
              label="Ruolo"
              value={draft.role}
              items={ROLES}
              onChange={(v) => setDraft({ ...draft, role: v })}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Annulla
              </Button>
              <Button
                variant="primary"
                icon="plus"
                loading={busy}
                disabled={!draft.user.trim() || draft.password.length < 8}
                onClick={create}
              >
                Crea profilo
              </Button>
            </div>
          </div>
        )}
      </div>
    </SettingsBlock>
  );
}
